import {
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ExpandMore } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import {
  ColorsConstants,
  grayFontColor,
} from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import {
  INPUT_TYPE_OPTIONS,
  MAX_RULES,
  NUMERIC_OPS,
  RULE_OPS,
  isChoiceType,
  isNumberField,
  isTextType,
  newOptionId,
  toFieldKey,
} from "./builderConstants";
import useFormImage from "./useFormImage";

const labelSx = {
  ...typography.bodySmall,
  fontWeight: 500,
  color: ColorsConstants.primaryBackgroundColor,
  mt: 1.5,
  mb: 0.5,
};

const fieldProps = {
  size: "small",
  fullWidth: true,
  InputProps: { sx: typography.bodyLarge },
};

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={labelSx}>{label}</Typography>
    {children}
  </Box>
);

// helperText/error props for a text with a WhatsApp length limit: a counter, which
// turns into an error as soon as the limit is passed (publishing is blocked too).
// showCounter=false only shows the error (e.g. option titles).
const limitProps = (value, max, showCounter = true) => {
  if (!max) return {};
  const length = (value || "").length;
  if (length > max) {
    return { error: true, helperText: `${length}/${max} · Too long: at most ${max} characters` };
  }
  return { helperText: showCounter ? `${length}/${max}` : undefined };
};

const ImageEditor = ({ component, formId, onChange, uploadImage, limits }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const url = useFormImage(formId, component.image_id);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      onChange({ image_id: await uploadImage(file) });
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Field label="Alt text">
        <TextField
          {...fieldProps}
          value={component.alt || ""}
          onChange={(e) => onChange({ alt: e.target.value })}
        />
      </Field>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
        {url && (
          <Box
            component="img"
            src={url}
            alt={component.alt || ""}
            sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: "6px" }}
          />
        )}
        <Button
          variant="outlined"
          size="small"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          sx={{ textTransform: "none" }}
        >
          {uploading ? "Uploading..." : component.image_id ? "Replace image" : "Upload image"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={handleFile}
        />
      </Stack>
      <Typography sx={{ ...typography.bodySmall, color: error ? "#b50000" : grayFontColor, mt: 0.5 }}>
        {error ||
          `Upload JPG or PNG, up to ${Math.round((limits?.max_image_bytes || 307200) / 1024)} KB.`}
      </Typography>
    </>
  );
};

const MoreSettings = ({ component, onChange, typeLimits }) => {
  const type = component.type;
  const number = (name, label) => (
    <Field label={label}>
      <TextField
        {...fieldProps}
        type="number"
        inputProps={{ min: 0 }}
        value={component[name] ?? ""}
        onChange={(e) => onChange({ [name]: e.target.value === "" ? null : e.target.value })}
      />
    </Field>
  );
  const pattern = (
    <Field label="Answer must match (regex)">
      <TextField
        {...fieldProps}
        value={component.pattern || ""}
        placeholder="e.g. [0-9]{6}"
        onChange={(e) => onChange({ pattern: e.target.value })}
      />
    </Field>
  );

  return (
    <>
      {["short_answer", "paragraph", "date"].includes(type) && (
        <Field label="Helper text">
          <TextField
            {...fieldProps}
            value={component.helper_text || ""}
            {...limitProps(component.helper_text, typeLimits?.helper_max, false)}
            onChange={(e) => onChange({ helper_text: e.target.value })}
          />
        </Field>
      )}
      {type === "short_answer" && (
        <>
          <Field label="Input type">
            <TextField
              {...fieldProps}
              select
              value={component.input_type || "text"}
              onChange={(e) => onChange({ input_type: e.target.value })}
            >
              {INPUT_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={typography.bodyLarge}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Field>
          {number("min_chars", "Minimum characters")}
          {number("max_chars", "Maximum characters")}
          {pattern}
        </>
      )}
      {type === "paragraph" && (
        <>
          {number("max_chars", "Maximum characters")}
          {pattern}
        </>
      )}
      {type === "date" && (
        <>
          <Field label="Earliest date">
            <TextField
              {...fieldProps}
              type="date"
              value={component.min_date || ""}
              onChange={(e) => onChange({ min_date: e.target.value || null })}
            />
          </Field>
          <Field label="Latest date">
            <TextField
              {...fieldProps}
              type="date"
              value={component.max_date || ""}
              onChange={(e) => onChange({ max_date: e.target.value || null })}
            />
          </Field>
        </>
      )}
      {type === "multiple_choice" && (
        <>
          {number("min_selected", "Minimum selections")}
          {number("max_selected", "Maximum selections")}
        </>
      )}
    </>
  );
};

const HAS_MORE_SETTINGS = ["short_answer", "paragraph", "date", "multiple_choice"];

const linkButtonSx = {
  ...typography.bodySmall,
  background: "none",
  border: 0,
  p: 0,
  mt: 1,
  cursor: "pointer",
  color: ColorsConstants.primaryPurple100,
  "&:disabled": { color: grayFontColor, cursor: "default" },
};

/**
 * "Show only when" conditions of a component. `ruleFields` are the questions it can
 * depend on (this screen's and earlier ones'), from ruleFieldsFor.
 */
const ShowOnlyWhen = ({ component, ruleFields, onChange }) => {
  const rules = component.visible_when || [];
  const fields = (ruleFields || []).filter((f) => f.component.id !== component.id);
  const setRules = (next) => onChange({ visible_when: next });
  const setRule = (index, patch) =>
    setRules(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  return (
    <Field label="Show only when">
      {rules.length === 0 && (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          {fields.length
            ? "Always shown. Add a condition to show it only for certain answers (all must match)."
            : "Always shown. Add a question above this one to make it conditional."}
        </Typography>
      )}
      <Stack spacing={1.25}>
        {rules.map((rule, index) => {
          const picked = fields.find((f) => f.component.id === rule.component_id);
          const numeric = isNumberField(picked?.component);
          const ops = Object.keys(RULE_OPS).filter(
            (op) => numeric || !NUMERIC_OPS.includes(op),
          );
          return (
            <Stack key={index} spacing={0.75}>
              <Stack direction="row" spacing={0.75}>
                <TextField
                  {...fieldProps}
                  select
                  value={picked ? rule.component_id : ""}
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ "aria-label": `Condition ${index + 1} question` }}
                  onChange={(e) =>
                    setRule(index, { component_id: e.target.value, op: "==", value: "" })
                  }
                >
                  <MenuItem value="" disabled sx={typography.bodyLarge}>
                    Pick a question
                  </MenuItem>
                  {fields.map(({ component: field, where }) => (
                    <MenuItem key={field.id} value={field.id} sx={typography.bodyLarge}>
                      {`${field.label || field.key || "Untitled"} (${where})`}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton
                  size="small"
                  aria-label="Remove condition"
                  onClick={() => setRules(rules.filter((_, i) => i !== index))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={0.75}>
                <TextField
                  {...fieldProps}
                  select
                  value={ops.includes(rule.op) ? rule.op : "=="}
                  inputProps={{ "aria-label": `Condition ${index + 1} comparison` }}
                  onChange={(e) => setRule(index, { op: e.target.value })}
                  sx={{ flex: 1 }}
                >
                  {ops.map((op) => (
                    <MenuItem key={op} value={op} sx={typography.bodyLarge}>
                      {RULE_OPS[op]}
                    </MenuItem>
                  ))}
                </TextField>
                {isChoiceType(picked?.component.type) ? (
                  <TextField
                    {...fieldProps}
                    select
                    value={rule.value || ""}
                    SelectProps={{ displayEmpty: true }}
                    inputProps={{ "aria-label": `Condition ${index + 1} answer` }}
                    onChange={(e) => setRule(index, { value: e.target.value })}
                    sx={{ flex: 1.5 }}
                  >
                    <MenuItem value="" disabled sx={typography.bodyLarge}>
                      Pick an answer
                    </MenuItem>
                    {(picked.component.options || []).map((option) => (
                      <MenuItem key={option.id} value={option.id} sx={typography.bodyLarge}>
                        {option.title}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    {...fieldProps}
                    type={numeric ? "number" : "text"}
                    value={rule.value || ""}
                    placeholder="Value"
                    inputProps={{ "aria-label": `Condition ${index + 1} value` }}
                    onChange={(e) => setRule(index, { value: e.target.value })}
                    sx={{ flex: 1.5 }}
                  />
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      <Box
        component="button"
        type="button"
        disabled={rules.length >= MAX_RULES || fields.length === 0}
        onClick={() => setRules([...rules, { component_id: "", op: "==", value: "" }])}
        sx={linkButtonSx}
      >
        + Add condition
      </Box>
    </Field>
  );
};

/**
 * Editor for one component of a screen. `onChange(patch)` merges into the component.
 * `lockedKey` is the field key frozen by an earlier publish, if any.
 */
const ComponentFields = ({
  component,
  formId,
  limits,
  lockedKey,
  keyOwners,
  screenIndex,
  onChange,
  uploadImage,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const type = component.type;
  const typeLimits = limits?.components?.[type] || {};
  // Another field already using this key (see fieldKeyOwners).
  const keyClash = (keyOwners?.[component.key] || []).find((f) => f.id !== component.id);

  if (isTextType(type)) {
    return (
      <Field label="Text">
        <TextField
          {...fieldProps}
          multiline
          minRows={2}
          value={component.text || ""}
          {...limitProps(component.text, typeLimits.text_max)}
          onChange={(e) => onChange({ text: e.target.value })}
        />
      </Field>
    );
  }

  if (type === "link") {
    return (
      <>
        <Field label="Link text">
          <TextField
            {...fieldProps}
            value={component.text || ""}
            {...limitProps(component.text, typeLimits.text_max)}
            onChange={(e) => onChange({ text: e.target.value })}
          />
        </Field>
        <Field label="Web address">
          <TextField
            {...fieldProps}
            value={component.url || ""}
            placeholder="https://example.com"
            helperText="Opens in the browser when the customer taps the link."
            onChange={(e) => onChange({ url: e.target.value })}
          />
        </Field>
      </>
    );
  }

  if (type === "image") {
    return (
      <ImageEditor
        component={component}
        formId={formId}
        onChange={onChange}
        uploadImage={uploadImage}
        limits={limits}
      />
    );
  }

  const options = component.options || [];
  const setOption = (index, title) =>
    onChange({ options: options.map((o, i) => (i === index ? { ...o, title } : o)) });

  return (
    <>
      <Field label="Label">
        <TextField
          {...fieldProps}
          value={component.label || ""}
          {...limitProps(component.label, typeLimits.label_max)}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </Field>

      {isChoiceType(type) && (
        <Field label="Options">
          <Stack spacing={0.75}>
            {options.map((option, index) => (
              <Stack key={option.id} direction="row" spacing={0.75}>
                <TextField
                  {...fieldProps}
                  value={option.title}
                  {...limitProps(option.title, typeLimits.option_title_max, false)}
                  inputProps={{ "aria-label": `Option ${index + 1}` }}
                  onChange={(e) => setOption(index, e.target.value)}
                />
                <IconButton
                  size="small"
                  aria-label="Remove option"
                  disabled={options.length <= (typeLimits.options_min || 1)}
                  onClick={() => onChange({ options: options.filter((_, i) => i !== index) })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Box
            component="button"
            type="button"
            disabled={options.length >= (typeLimits.options_max || 20)}
            onClick={() =>
              onChange({
                options: [
                  ...options,
                  { id: newOptionId(), title: `Option ${options.length + 1}` },
                ],
              })
            }
            sx={{
              ...typography.bodySmall,
              background: "none",
              border: 0,
              p: 0,
              mt: 1,
              cursor: "pointer",
              color: ColorsConstants.primaryPurple100,
              "&:disabled": { color: grayFontColor, cursor: "default" },
            }}
          >
            + Add option
          </Box>
        </Field>
      )}

      <Field label="Field key">
        <TextField
          {...fieldProps}
          value={component.key || ""}
          disabled={Boolean(lockedKey)}
          placeholder="Generated from the label"
          onChange={(e) => onChange({ key: toFieldKey(e.target.value) })}
          error={Boolean(keyClash)}
          helperText={
            keyClash
              ? `Already used by "${keyClash.label || "another field"}" ${
                  keyClash.screenIndex === screenIndex
                    ? "on this screen"
                    : `on screen ${keyClash.screenIndex + 1}`
                }. Each field needs its own key.`
              : lockedKey
                ? "Locked after the first publish."
                : "Saved with each submission and used for contact mapping."
          }
        />
      </Field>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
        <Typography sx={typography.bodySmall}>Required</Typography>
        <Switch
          size="small"
          checked={Boolean(component.required)}
          onChange={(e) => onChange({ required: e.target.checked })}
          inputProps={{ "aria-label": "Required" }}
        />
      </Stack>

      {HAS_MORE_SETTINGS.includes(type) && (
        <Box sx={{ mt: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setMoreOpen((open) => !open)}
            aria-expanded={moreOpen}
            sx={{
              ...typography.bodySmall,
              background: "none",
              border: 0,
              p: 0,
              cursor: "pointer",
              color: ColorsConstants.primaryPurple100,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            More settings
            <ExpandMore
              fontSize="small"
              sx={{ transform: moreOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
          </Box>
          <Collapse in={moreOpen} unmountOnExit>
            <MoreSettings component={component} onChange={onChange} typeLimits={typeLimits} />
          </Collapse>
        </Box>
      )}
    </>
  );
};

// The component's own settings plus its "Show only when" conditions;
// `ruleFields` are the questions those conditions can use.
const ComponentEditor = ({ ruleFields, ...props }) => (
  <>
    <ComponentFields {...props} />
    <ShowOnlyWhen
      component={props.component}
      ruleFields={ruleFields}
      onChange={props.onChange}
    />
  </>
);

export default ComponentEditor;
